import { useEffect, useState } from 'react';
import { Bell, CheckCheck, Loader2, Trash2, Check, Package, ShoppingBag, XCircle, AlertTriangle, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AdminLayout } from '@/components/layout/AdminLayout';
import {
  adminGetNotifications,
  adminMarkNotificationRead,
  adminMarkAllNotificationsRead,
  adminDeleteNotification,
} from '@/lib/api';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const TYPE_META = {
  new_order: { label: 'New Orders', icon: ShoppingBag, color: 'text-green-700 bg-green-100' },
  status_update: { label: 'Status Updates', icon: Package, color: 'text-blue-700 bg-blue-100' },
  cancellation: { label: 'Cancellations', icon: XCircle, color: 'text-red-700 bg-red-100' },
  low_inventory: { label: 'Low Inventory', icon: AlertTriangle, color: 'text-amber-700 bg-amber-100' },
  payment_failed: { label: 'Payment Failures', icon: CreditCard, color: 'text-red-700 bg-red-100' },
};

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function AdminNotifications() {
  const { isAuthenticated, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/admin');
  }, [authLoading, isAuthenticated, navigate]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (unreadOnly) params.unread_only = true;
      if (typeFilter !== 'all') params.n_type = typeFilter;
      const res = await adminGetNotifications(params);
      setItems(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Could not load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, unreadOnly, typeFilter]);

  const handleMarkOne = async (id) => {
    try {
      await adminMarkNotificationRead(id);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      window.dispatchEvent(new Event('notifications:refresh'));
    } catch {
      toast.error('Failed to mark read');
    }
  };

  const handleMarkAll = async () => {
    setMarking(true);
    try {
      await adminMarkAllNotificationsRead();
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      window.dispatchEvent(new Event('notifications:refresh'));
      toast.success('All marked as read');
    } catch {
      toast.error('Failed to mark all');
    } finally {
      setMarking(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notification?')) return;
    try {
      await adminDeleteNotification(id);
      setItems((prev) => prev.filter((n) => n.id !== id));
      window.dispatchEvent(new Event('notifications:refresh'));
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const unreadCount = items.filter((n) => !n.is_read).length;

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto" data-testid="admin-notifications-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Bell className="w-6 h-6" />
              Notifications
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {items.length} total · {unreadCount} unread
            </p>
          </div>
          <Button
            onClick={handleMarkAll}
            disabled={marking || unreadCount === 0}
            className="rounded-full"
            data-testid="page-mark-all-read-btn"
          >
            {marking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCheck className="w-4 h-4 mr-2" />}
            Mark All Read
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4 bg-white border border-border rounded-xl p-3">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[200px]" data-testid="filter-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(TYPE_META).map(([key, meta]) => (
                <SelectItem key={key} value={key}>{meta.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
              className="rounded h-4 w-4"
              data-testid="filter-unread-only"
            />
            Unread only
          </label>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-border rounded-xl py-16 text-center" data-testid="notifications-empty">
            <Bell className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-semibold">No notifications</p>
            <p className="text-sm text-muted-foreground">
              {unreadOnly || typeFilter !== 'all'
                ? 'Try clearing filters'
                : 'You\'ll see new orders, status changes, and alerts here'}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden divide-y" data-testid="notifications-list">
            {items.map((n) => {
              const meta = TYPE_META[n.type] || TYPE_META.new_order;
              const Icon = meta.icon;
              return (
                <div
                  key={n.id}
                  className={cn(
                    'flex items-start gap-4 p-4 hover:bg-muted/40 transition',
                    !n.is_read && 'bg-primary/5'
                  )}
                  data-testid={`page-notification-${n.id}`}
                >
                  <div className={cn('w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0', meta.color)}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{n.title}</h3>
                      {!n.is_read && (
                        <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-bold">NEW</span>
                      )}
                      <span className="text-[10px] uppercase text-muted-foreground font-semibold">{n.severity}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{n.message}</p>
                    <p className="text-xs text-muted-foreground mt-2">{formatTime(n.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!n.is_read && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleMarkOne(n.id)}
                        title="Mark as read"
                        data-testid={`page-mark-read-${n.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(n.id)}
                      className="text-destructive hover:text-destructive"
                      title="Delete"
                      data-testid={`page-delete-${n.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
